import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CircleCheck, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type EditAvatarResponse = {
  avatarUrl: string | null
  listApplications: {
    url: string
    method: 'POST'
    headers: {
      Authorization: string
      locale: string
    }
  }[]
}

async function editAvatar(data: FormData) {
  const response = await api.backend.post<EditAvatarResponse>(
    '/user/edit-avatar',
    data,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )

  if (
    !response.data.listApplications ||
    response.data.listApplications.length === 0
  ) {
    return
  }

  const subsequentRequests = response.data.listApplications.map(
    async (application) => {
      try {
        const fetchResponse = await fetch(application.url, {
          body: data,
          method: application.method,
          headers: application.headers,
        })

        if (!fetchResponse.ok) {
          Sentry.captureMessage(
            `Error on subsequent avatar upload request to ${application.url}: ${fetchResponse.status}`,
            'error',
          )
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    },
  )

  await Promise.allSettled(subsequentRequests)
}

export function useEditAvatar() {
  const { t } = useTranslation('profile')
  const { mutateAsync, isPending } = useMutation<
    void,
    AxiosError<ApiError>,
    FormData
  >({
    mutationKey: ['/user/edit-avatar'],
    mutationFn: editAvatar,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CircleCheck,
          title: t('general_data.toast.data.success.title'),
          description: t('general_data.toast.data.success.message'),
        },
      })
      queryClient.refetchQueries({ queryKey: ['/user/get-user-info'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('general_data.toast.avatar.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('general_data.toast.avatar.error.message'),
        },
      })
    },
  })

  const handleEditAvatar = async (file: File) => {
    const data = new FormData()
    data.append('file', file)
    await mutateAsync(data)
  }

  return {
    handleEditAvatar,
    isPendingEditAvatar: isPending,
  }
}
